import React from 'react';
import {
  AlertTriangle, CalendarClock, Calendar, Wallet, Receipt,
} from 'lucide-react';

/**
 * Bills Due Hub — top KPI strip for the Bills Center workspace.
 *
 * Receives the payload from ``GET /api/bills/due-hub``:
 *
 *   { today, cards: { overdue, due_this_week, due_this_month,
 *     total_payable }, by_kind: [{bill_kind, count, amount}] }
 *
 * Each card click drills the table below to the matching slice via
 * the ``onFilter`` callback (parent owns the filter state).
 */
const fmt = (v) =>
  Number(v ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const Card = ({ icon: Icon, label, count, amount, tone, onClick, active, testid }) => {
  const tones = {
    red:    'border-red-300 bg-red-50/60 text-red-900 hover:bg-red-100',
    amber:  'border-amber-300 bg-amber-50/60 text-amber-900 hover:bg-amber-100',
    blue:   'border-blue-300 bg-blue-50/60 text-blue-900 hover:bg-blue-100',
    indigo: 'border-indigo-300 bg-indigo-50/60 text-indigo-900 hover:bg-indigo-100',
  }[tone];
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-start rounded-xl border p-4 transition-all ${tones} ${
        active ? 'ring-2 ring-offset-2 ring-indigo-500 shadow-md' : ''
      }`}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide font-semibold opacity-80">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-2xl font-bold">{count ?? 0}</span>
        <span className="text-xs opacity-70">فاتورة</span>
      </div>
      <div className="text-xs mt-1 font-mono">{fmt(amount)} SAR</div>
    </button>
  );
};

export const BillsDueHub = ({ data, onFilter, activeFilter }) => {
  const c = data?.cards || {};
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bills-due-hub">
      <Card
        icon={AlertTriangle}
        label="مُتَأَخِّر"
        count={c.overdue?.count}
        amount={c.overdue?.amount}
        tone="red"
        active={activeFilter === 'overdue'}
        onClick={() => onFilter('overdue')}
        testid="due-card-overdue"
      />
      <Card
        icon={CalendarClock}
        label="مُستَحَق هَذا الأُسبوع"
        count={c.due_this_week?.count}
        amount={c.due_this_week?.amount}
        tone="amber"
        active={activeFilter === 'due_this_week'}
        onClick={() => onFilter('due_this_week')}
        testid="due-card-week"
      />
      <Card
        icon={Calendar}
        label="مُستَحَق هَذا الشَّهر"
        count={c.due_this_month?.count}
        amount={c.due_this_month?.amount}
        tone="blue"
        active={activeFilter === 'due_this_month'}
        onClick={() => onFilter('due_this_month')}
        testid="due-card-month"
      />
      <Card
        icon={Wallet}
        label="إجمالي المُستَحَق"
        count={c.total_payable?.count}
        amount={c.total_payable?.amount}
        tone="indigo"
        active={activeFilter === 'all'}
        onClick={() => onFilter('all')}
        testid="due-card-total"
      />
    </div>
  );
};

export default BillsDueHub;
